import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HighlightTemplate, SlideLayoutProps } from '../types';
import {
  BodyContent,
  LEADING,
  PADDING,
  SlideText,
  TYPE,
  fitTitle,
  formatPageLabel,
  hasNextSlide,
  shouldShowCounter,
} from '../_shared';

// "Editorial Serif" - high-contrast Playfair display titles on a soft
// cream surface, with sans-serif support text. Footer rule + caption +
// arrow pill / counter pair drives wayfinding.

const SURFACE = 'bg-[#F8F4E6]';
const INK = 'text-black';
const RULE = 'bg-black';

function NextPill() {
  return (
    <div
      className='pointer-events-none flex h-9 w-16 items-center justify-center rounded-full border border-black md:h-10 md:w-20'
      aria-hidden='true'
    >
      <ArrowRight className='h-4 w-4 text-black' strokeWidth={1.75} />
    </div>
  );
}

function ConceptCaption({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <p
      className={cn(
        'font-medium uppercase tracking-[0.22em] text-black/70',
        TYPE.caption,
        LEADING.caption
      )}
    >
      <SlideText text={text} />
    </p>
  );
}

function PageCounter({ label }: { label: string }) {
  return (
    <span className={cn('font-medium text-black/80', TYPE.page)}>{label}</span>
  );
}

function CoverLayout({
  card,
  slideIndex,
  totalSlides,
  conceptName,
}: SlideLayoutProps) {
  return (
    <div className={cn('relative flex h-full w-full flex-col', SURFACE)}>
      <div
        className={cn(
          'flex flex-1 flex-col gap-6',
          PADDING.slideX,
          PADDING.slideTop
        )}
      >
        <h1
          className={cn(
            'text-balance font-display font-bold tracking-tight',
            INK,
            fitTitle(card.title),
            LEADING.display
          )}
        >
          <SlideText text={card.title} />
        </h1>

        {card.body && (
          <div className='mt-2 max-w-2xl'>
            <BodyContent
              body={card.body}
              visual={card.visual}
              className={INK}
            />
          </div>
        )}
      </div>

      <div className='mx-10 md:mx-14'>
        <div className={cn('h-px w-full', RULE)} />
      </div>

      <div
        className={cn(
          'flex items-center justify-between py-6 md:py-8',
          PADDING.slideX
        )}
      >
        <ConceptCaption text={conceptName} />
        {hasNextSlide(slideIndex, totalSlides) && <NextPill />}
      </div>
    </div>
  );
}

function ContentLayout({
  card,
  slideIndex,
  totalSlides,
  conceptName,
}: SlideLayoutProps) {
  const showCounter = shouldShowCounter(slideIndex, totalSlides);
  const showArrow = hasNextSlide(slideIndex, totalSlides);

  return (
    <div className={cn('relative flex h-full w-full flex-col', SURFACE)}>
      <div
        className={cn(
          'flex flex-1 flex-col gap-6',
          PADDING.slideX,
          PADDING.slideTop
        )}
      >
        <h2
          className={cn(
            'text-balance font-display font-bold tracking-tight',
            INK,
            fitTitle(card.title, 'body'),
            LEADING.display
          )}
        >
          <SlideText text={card.title} />
        </h2>

        {card.body && (
          <div className='mt-2 max-w-2xl'>
            <BodyContent
              body={card.body}
              visual={card.visual}
              className={INK}
            />
          </div>
        )}
      </div>

      <div className='mx-10 md:mx-14'>
        <div className={cn('h-px w-full', RULE)} />
      </div>

      <div
        className={cn(
          'flex items-center justify-between gap-4 py-6 md:py-8',
          PADDING.slideX
        )}
      >
        <ConceptCaption text={conceptName} />
        <div className='flex items-center gap-4'>
          {showCounter && (
            <PageCounter label={formatPageLabel(slideIndex, totalSlides)} />
          )}
          {showArrow && <NextPill />}
        </div>
      </div>
    </div>
  );
}

function ThumbnailLayout({ card }: SlideLayoutProps) {
  return (
    <div className={cn('relative flex h-full w-full flex-col p-5', SURFACE)}>
      <div className='flex flex-1 flex-col justify-center'>
        <h3
          className={cn(
            'line-clamp-4 font-display font-bold tracking-tight',
            INK,
            fitTitle(card.title, 'thumb'),
            LEADING.display
          )}
        >
          <SlideText text={card.title} />
        </h3>
      </div>

      <div className={cn('h-px w-full', RULE)} />
    </div>
  );
}

export const Template01: HighlightTemplate = {
  id: 'template-01',
  name: 'Editorial Serif',
  layouts: {
    cover: CoverLayout,
    body: ContentLayout,
    summary: ContentLayout,
  },
  thumbnail: ThumbnailLayout,
};
